/**
 * SendSmsDialog — réplica de la herramienta "Send SMS" de Zicasso.
 * Envía un SMS real al viajero (POST /zicasso/leads/{id}/send-sms) desde el
 * número Zicasso del partner. Incluye: plantillas de SMS, borrador IA (versión
 * corta), contador 640, prefijo "Zicasso: ", aviso si el número no es de EE. UU.
 * o no hay teléfono, y (en modo Foco) "Enviar y completar" la tarea.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquareText, Sparkles, Save, Send, Loader2, Phone, AlertTriangle } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { SMS } from "@/lib/features";

const firstName = (s = "") => (s || "").trim().split(" ")[0] || "";

function renderTemplate(body, { lead, user }) {
  const dest = (lead?.countries || []).join(", ");
  return (body || "")
    .replaceAll("{{name}}", firstName(lead?.traveler_name))
    .replaceAll("{{agent}}", firstName(user?.full_name))
    .replaceAll("{{destination}}", dest || "your trip")
    .replaceAll("{{phone}}", SMS.companyPhone)
    .replaceAll("{{ext}}", user?.sms_call_ext || "");
}

function phoneInfo(lead) {
  let prefix = lead?.traveler_phone_prefix || "";
  let number = lead?.traveler_phone_number || "";
  if (!number) {
    const t = (lead?.raw_trip_match?.travelers || [])[0] || {};
    prefix = prefix || t?.phone?.prefix || "";
    number = number || t?.phone?.number || "";
  }
  const digits = String(prefix).replace(/\D/g, "");
  const hasPhone = !!String(number).trim();
  const isUs = digits === "1" || digits === "";
  const formatted = hasPhone ? (digits ? `+${digits} ${number}` : String(number)) : "";
  return { hasPhone, isUs, formatted };
}

function localTimeLabel(tz) {
  if (!tz) return null;
  try {
    const t = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(new Date());
    const city = tz.split("/").pop().replace(/_/g, " ");
    return `${t} in ${city}`;
  } catch {
    return null;
  }
}

export function SendSmsDialog({ open, onOpenChange, lead, taskId, agentId, onSent, onCompleted }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [message, setMessage] = useState(SMS.prefix);
  const [tplId, setTplId] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const templatesQ = useQuery({
    queryKey: ["sms-templates"],
    queryFn: async () => (await api.get("/zicasso/sms-templates")).data,
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => { if (open) { setMessage(SMS.prefix); setTplId(""); } }, [open, lead?.id]);

  const ph = useMemo(() => phoneInfo(lead || {}), [lead]);
  const len = message.length;
  const over = len > SMS.maxLength;
  const onlyPrefix = message.trim() === SMS.prefix.trim() || len <= SMS.prefix.length;
  const blocked = !ph.hasPhone
    ? "Este contacto no tiene teléfono guardado."
    : !ph.isUs
      ? "Zicasso solo permite SMS a números de EE. UU. (+1)."
      : null;
  const canSend = !blocked && !over && !onlyPrefix;

  const applyTemplate = (id) => {
    setTplId(id);
    const t = (templatesQ.data || []).find((x) => x.id === id);
    if (t) setMessage(SMS.prefix + renderTemplate(t.body, { lead, user }));
  };

  const genAi = async () => {
    try {
      setAiLoading(true);
      const r = await api.post(`/zicasso/leads/${lead.id}/ai-draft`, {
        instructions:
          `Write a very short SMS (max 2 lines, under 300 characters) in English. Start by identifying yourself as ${firstName(user?.full_name) || "the traveler's trip organizer"}, the traveler's trip organizer at Espíritu Travel. Friendly, concise follow-up. No subject line and no signature block.`,
      });
      setMessage(SMS.prefix + (r.data?.text || "").trim());
    } catch (e) {
      toast.error(apiErrorMessage(e, "No se pudo generar con IA"));
    } finally {
      setAiLoading(false);
    }
  };

  const saveTplMut = useMutation({
    mutationFn: (payload) => api.post("/zicasso/sms-templates", payload),
    onSuccess: () => { toast.success("Plantilla guardada"); qc.invalidateQueries({ queryKey: ["sms-templates"] }); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo guardar la plantilla")),
  });
  const saveAsTemplate = () => {
    const name = window.prompt("Nombre de la plantilla:");
    if (!name) return;
    let body = message;
    if (body.startsWith(SMS.prefix)) body = body.slice(SMS.prefix.length);
    saveTplMut.mutate({ name: name.trim(), body: body.trim() });
  };

  const sendMut = useMutation({
    mutationFn: () => api.post(`/zicasso/leads/${lead.id}/send-sms`, { text: message }),
    onSuccess: (res) => {
      toast.success(res.data?.demo ? "SMS registrado (demo)" : `SMS enviado a ${res.data?.to || "el viajero"}`);
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      onSent?.(res.data);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo enviar el SMS")),
  });

  const sendCompleteMut = useMutation({
    mutationFn: () => {
      const q = agentId && agentId !== "__all__" ? `?agent_id=${agentId}` : "";
      return api.post(`/zicasso/tasks/${taskId}/complete${q}`, { action: "sms", body: message });
    },
    onSuccess: (res) => {
      toast.success(res.data?.demo ? "SMS registrado y tarea completada (demo)" : "SMS enviado y tarea completada");
      onOpenChange(false);
      onCompleted?.(res.data);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo enviar/completar")),
  });

  const sending = sendMut.isPending || sendCompleteMut.isPending;
  const counterCaption = over ? "Demasiado largo" : "Mantenlo corto. Los textos de menos de 2 líneas reciben respuesta más rápido.";
  const localT = localTimeLabel(lead?.traveler_timezone);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-sms-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="h-4 w-4" /> Enviar SMS
          </DialogTitle>
          <DialogDescription>
            Envía un texto cuando el viajero no responde. Haz seguimiento con timing, sin repetir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Plantilla</Label>
            <Select value={tplId} onValueChange={applyTemplate}>
              <SelectTrigger className="mt-1" data-testid="sms-template-select">
                <SelectValue placeholder="Elige una plantilla…" />
              </SelectTrigger>
              <SelectContent>
                {(templatesQ.data || []).map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`sms-template-option-${t.id}`}>{t.name}</SelectItem>
                ))}
                {(templatesQ.data || []).length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">No hay plantillas. Créalas en Ajustes.</div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mensaje</Label>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={genAi} disabled={aiLoading} data-testid="sms-ai-button">
                  {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} IA
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={saveAsTemplate} disabled={onlyPrefix || saveTplMut.isPending} data-testid="sms-save-template-button">
                  <Save className="h-3 w-3" /> Guardar
                </Button>
              </div>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="mt-1"
              data-testid="sms-message-textarea"
            />
            <p className={`mt-1 text-[11px] ${over ? "text-[#C45A45]" : "text-muted-foreground"}`} data-testid="sms-counter">
              {len}/{SMS.maxLength} | {counterCaption}
            </p>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Se envía desde tu número de Zicasso {SMS.fromNumber}. Las respuestas llegan a esta conversación.
          </p>

          {blocked ? (
            <div className="flex items-start gap-2 rounded-md bg-[#F7E7E3] px-3 py-2 text-[12px] text-[#803D26]" data-testid="sms-blocked">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {blocked}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md bg-secondary/50 px-3 py-2 text-[12px]" data-testid="sms-recipient">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              Para {lead?.traveler_name || "el viajero"} ({ph.formatted}){localT ? ` • ${localT}` : ""}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="sms-cancel-button">Cancelar</Button>
          {taskId && (
            <Button variant="outline" onClick={() => sendCompleteMut.mutate()} disabled={!canSend || sending} data-testid="sms-send-complete-button">
              {sendCompleteMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Enviar y completar
            </Button>
          )}
          <Button onClick={() => sendMut.mutate()} disabled={!canSend || sending} data-testid="sms-send-button">
            {sendMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendSmsDialog;
